import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '@/api/client';

// Shared global state so all hook consumers update simultaneously
let globalIsOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let isCheckingGlobal = false;
const listeners = new Set<(online: boolean) => void>();
let pollTimer: ReturnType<typeof setTimeout> | null = null;

function setGlobalOnline(online: boolean) {
  if (globalIsOnline !== online) {
    globalIsOnline = online;
    listeners.forEach((l) => l(online));
  }
}

async function probeBackendReachability(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    setGlobalOnline(false);
    return false;
  }

  if (isCheckingGlobal) return globalIsOnline;
  isCheckingGlobal = true;

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);

    const isNative = typeof window !== 'undefined' && Boolean((window as any).Capacitor?.isNativePlatform?.());
    const probeUrl = `${getApiBaseUrl()}/manifest.json?_t=${Date.now()}`;
    const res = await fetch(probeUrl, {
      method: isNative ? 'GET' : 'HEAD',
      mode: isNative ? 'no-cors' : 'cors',
      signal: controller.signal,
      cache: 'no-store',
    }).catch(() => {
      return fetch(`/?_t=${Date.now()}`, {
        method: isNative ? 'GET' : 'HEAD',
        mode: isNative ? 'no-cors' : 'cors',
        signal: controller.signal,
        cache: 'no-store',
      });
    });

    clearTimeout(timer);
    const reachable = isNative
      ? (res.type === 'opaque' || res.ok || (res.status > 0 && res.status < 500))
      : (res.ok || res.status < 500);
    setGlobalOnline(reachable);
    return reachable;
  } catch {
    // Unreachable (e.g. isolated logger AP or no signal)
    setGlobalOnline(false);
    return false;
  } finally {
    isCheckingGlobal = false;
  }
}

function scheduleNextCheck() {
  if (pollTimer) {
    clearTimeout(pollTimer);
  }
  // While offline: fast check every 2.5 seconds to instantly detect internet return
  // While online: relaxed background check every 30 seconds
  const interval = globalIsOnline ? 30000 : 2500;
  pollTimer = setTimeout(async () => {
    await probeBackendReachability();
    scheduleNextCheck();
  }, interval);
}

// Global event listeners initialized once
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    probeBackendReachability().then(() => scheduleNextCheck());
  });

  window.addEventListener('offline', () => {
    setGlobalOnline(false);
    scheduleNextCheck();
  });

  window.addEventListener('network:offline', () => {
    setGlobalOnline(false);
    scheduleNextCheck();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      probeBackendReachability().then(() => scheduleNextCheck());
    }
  });

  window.addEventListener('focus', () => {
    probeBackendReachability().then(() => scheduleNextCheck());
  });

  // Initial startup probe
  probeBackendReachability().then(() => scheduleNextCheck());
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(globalIsOnline);
  const [isChecking, setIsChecking] = useState<boolean>(isCheckingGlobal);

  useEffect(() => {
    setIsOnline(globalIsOnline);

    const handler = (online: boolean) => {
      setIsOnline(online);
    };

    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  const checkNow = useCallback(async (): Promise<boolean> => {
    setIsChecking(true);
    try {
      const res = await probeBackendReachability();
      scheduleNextCheck();
      return res;
    } finally {
      setIsChecking(false);
    }
  }, []);

  return {
    isOnline,
    isChecking,
    checkNow,
  };
}
